import { Check, AlertTriangle, Zap } from 'lucide-react'
import { cn } from '@/lib/utils'

interface TraitsDisplayProps {
  scandal_prone: number
  ambition: number
  traits?: string[]
  className?: string
}

interface TraitLevel {
  min: number
  max: number
  label: string
  description: string
  colorClass: string
  bgClass: string
  icon: 'check' | 'warning' | 'zap'
}

const SCANDAL_LEVELS: TraitLevel[] = [
  {
    min: 0,
    max: 25,
    label: 'Clean record',
    description: 'Low probability of scandal events',
    colorClass: 'text-green-600 dark:text-green-400',
    bgClass: 'bg-green-500',
    icon: 'check',
  },
  {
    min: 26,
    max: 50,
    label: 'Minor risks',
    description: 'Some potential for controversy',
    colorClass: 'text-yellow-600 dark:text-yellow-400',
    bgClass: 'bg-yellow-500',
    icon: 'warning',
  },
  {
    min: 51,
    max: 75,
    label: 'Prone to controversy',
    description: 'Elevated scandal risk, requires monitoring',
    colorClass: 'text-orange-600 dark:text-orange-400',
    bgClass: 'bg-orange-500',
    icon: 'warning',
  },
  {
    min: 76,
    max: 100,
    label: 'High scandal risk',
    description: 'Very likely to cause problems',
    colorClass: 'text-red-600 dark:text-red-400',
    bgClass: 'bg-red-500',
    icon: 'warning',
  },
]

const AMBITION_LEVELS: TraitLevel[] = [
  {
    min: 0,
    max: 30,
    label: 'Content',
    description: 'Happy in any role, no advancement pressure',
    colorClass: 'text-green-600 dark:text-green-400',
    bgClass: 'bg-green-500',
    icon: 'check',
  },
  {
    min: 31,
    max: 60,
    label: 'Motivated',
    description: 'Wants advancement but patient',
    colorClass: 'text-blue-600 dark:text-blue-400',
    bgClass: 'bg-blue-500',
    icon: 'zap',
  },
  {
    min: 61,
    max: 80,
    label: 'Ambitious',
    description: 'Expects advancement, may resent being ignored',
    colorClass: 'text-yellow-600 dark:text-yellow-400',
    bgClass: 'bg-yellow-500',
    icon: 'zap',
  },
  {
    min: 81,
    max: 100,
    label: 'Very Ambitious',
    description: 'Demands prominent roles, loyalty at risk if overlooked',
    colorClass: 'text-orange-600 dark:text-orange-400',
    bgClass: 'bg-orange-500',
    icon: 'warning',
  },
]

function getTraitLevel(value: number, levels: TraitLevel[]): TraitLevel {
  const clamped = Math.max(0, Math.min(100, value))
  for (const level of levels) {
    if (clamped >= level.min && clamped <= level.max) {
      return level
    }
  }
  return levels[levels.length - 1]
}

function TraitIcon({ icon, className }: { icon: TraitLevel['icon']; className?: string }) {
  switch (icon) {
    case 'check':
      return <Check className={cn('h-4 w-4', className)} />
    case 'warning':
      return <AlertTriangle className={cn('h-4 w-4', className)} />
    case 'zap':
      return <Zap className={cn('h-4 w-4', className)} />
  }
}

interface TraitBarProps {
  label: string
  value: number
  levels: TraitLevel[]
}

function TraitBar({ label, value, levels }: TraitBarProps) {
  const level = getTraitLevel(value, levels)

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">{label}</span>
        <span className="text-sm font-semibold">{value}</span>
      </div>

      <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
        <div
          className={cn('h-full transition-all', level.bgClass)}
          style={{ width: `${value}%` }}
        />
      </div>

      <div className="flex items-center gap-1.5">
        <TraitIcon icon={level.icon} className={level.colorClass} />
        <span className={cn('text-sm font-medium', level.colorClass)}>
          {level.label}
        </span>
      </div>

      <p className="text-xs text-muted-foreground">{level.description}</p>
    </div>
  )
}

export function TraitsDisplay({
  scandal_prone,
  ambition,
  traits = [],
  className,
}: TraitsDisplayProps) {
  return (
    <div className={cn('space-y-4', className)}>
      <TraitBar label="Scandal Risk" value={scandal_prone} levels={SCANDAL_LEVELS} />
      <TraitBar label="Ambition" value={ambition} levels={AMBITION_LEVELS} />

      {traits.length > 0 && (
        <div className="space-y-2 border-t pt-3">
          <span className="text-sm font-medium">Additional Traits</span>
          <div className="flex flex-wrap gap-2">
            {traits.map((trait) => (
              <span
                key={trait}
                className="rounded-md bg-muted px-2 py-1 text-xs font-medium"
              >
                {trait}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
